import axios, { AxiosError } from 'axios';
import type { 
  CrewMember, Vessel, VesselCategory, DashboardStats, SearchResult, 
  ReliefPlan, DashboardOverview, Alert, AnalyticsOverview, RankDistribution,
  NationalityDistribution, VesselCoverage, WorkflowStats, CrewExperience,
  ExperienceMatrixEntry, ExperienceByCategory, ActivityLogEntry,
  CrewDetailResponse, VesselDetailResponse, Assignment, Rank, StatusOption,
  VesselFormData, CrewFormData, ReliefPlanFormData,
  VesselCrewPlan, CrewSuggestion, VesselCrewPlanConfirmation, VesselCrewStatus,
  VesselPositionSuggestionsResponse, VesselCreateWithPositions, VesselCreateWithPositionsResponse,
  AIBulkSuggestResponse, AIBulkExecuteResponse, AIReliefScheduleResponse,
  AICreateFromScheduleResponse, AIOptimizeTimingResponse
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    console.error('❌ Response Error:', error.response?.status, error.message);
    throw error;
  }
);

// ========================
// Transform Functions
// ========================

interface BackendSearchResponse {
  query: string;
  intent?: string;  // Query intent: crew_search, vessel_info, statistics, etc.
  parsed_params?: {
    rank?: string;
    vessel_category?: string;
    min_experience_months?: number;
    availability_days?: number;
    nationality?: string;
  };
  total_results?: number;
  total_vessels?: number;
  total_plans?: number;
  answer?: string;  // Natural language answer
  recommendations?: Array<{
    crew_id: string;
    name: string;
    surname?: string;
    rank: string;
    nationality: string;
    current_vessel?: string;
    due_date?: string;
    match_score: number;
    match_reasons?: string[];
    availability_status?: string;
    experience_summary?: string;
    experience_months?: number;
    contracts?: number;
    crew_type?: string;
    vessel_type?: string;
    status?: string;
  }>;
  vessels?: Array<any>;
  relief_plans?: Array<any>;
  summary?: any;
  dashboard?: any;
  analysis?: string;
}

interface BackendCrewResponse {
  total: number;
  limit?: number;
  offset?: number;
  crew: Array<{
    id?: number;
    cid: string;
    name?: string;
    surname?: string;
    rank: string;
    nationality?: string;
    vessel?: string;
    current_vessel?: string;
    due_date?: string;
    due_soff_date?: string;
    sign_on_date?: string;
    status?: string;
    readiness?: string;
    crew_type?: string;
    vessel_type?: string;
    next_vessel?: string;
    fleet?: string;
    company?: string;
    movement_type?: string;
    source_sheet?: string;
  }>;
}

function transformSearchResponse(data: BackendSearchResponse): SearchResult {
  // Handle different response types based on query intent
  const intent = data.intent || 'crew_search';
  
  // Build analysis message based on response type
  let analysis = data.analysis || data.answer || '';
  
  // If no crew recommendations but has other data, create a helpful message
  if (!data.recommendations || data.recommendations.length === 0) {
    if (intent === 'vessel_info' && data.vessels) {
      analysis = data.answer || `Found ${data.total_vessels || data.vessels.length} vessels. This is a vessel query - crew search results not applicable.`;
    } else if (intent === 'statistics' && data.summary) {
      analysis = data.answer || `Statistics query completed. ${data.summary.total_vessels || 0} vessels, ${data.summary.total_active || 0} active crew.`;
    } else if (intent === 'relief_plans' && data.relief_plans) {
      analysis = data.answer || `Found ${data.total_plans || data.relief_plans.length} relief plans.`;
    } else if (intent === 'general_info') {
      analysis = data.answer || 'General information query - no crew search results.';
    }
  }
  
  // Transform recommendations if they exist
  const recommendations = (data.recommendations || []).map((rec) => ({
    crew_id: rec.crew_id,
    cid: rec.crew_id,
    rank: rec.rank,
    name: rec.name || '',
    surname: rec.surname || '',
    nationality: rec.nationality || 'N/A',
    current_vessel: rec.current_vessel || 'N/A',
    vessel: rec.current_vessel || 'N/A',
    due_date: rec.due_date,
    due_soff_date: rec.due_date,
    match_score: rec.match_score || 0,
    match_reasons: rec.match_reasons || [],
    availability_status: rec.availability_status || '',
    experience_summary: rec.experience_summary || '',
    experience_months: rec.experience_months || 0,
    contracts: rec.contracts || 0,
    crew_type: rec.crew_type || rec.vessel_type || '',
    vessel_type: rec.vessel_type || rec.crew_type || '',
    status: rec.status || '',
  }));
  
  return {
    query: data.query,
    parsed_params: data.parsed_params || {},
    total_results: data.total_results || recommendations.length,
    recommendations,
    analysis,
    // Pass through additional data for rich display
    summary: data.summary,
    dashboard: data.dashboard,
    intent: data.intent,
    vessels: data.vessels,
    relief_plans: data.relief_plans,
  } as any; // Use 'as any' to allow additional properties
}

function transformCrewResponse(data: BackendCrewResponse): { total: number; crew: CrewMember[] } {
  return {
    total: data.total,
    crew: data.crew.map((c) => ({
      id: c.id,
      cid: c.cid,
      crew_id: c.cid,
      rank: c.rank,
      name: c.name || '',
      surname: c.surname || '',
      nationality: c.nationality || 'N/A',
      vessel: c.vessel || c.current_vessel || 'N/A',
      current_vessel: c.current_vessel || c.vessel || 'N/A',
      due_date: c.due_date || c.due_soff_date,
      due_soff_date: c.due_soff_date || c.due_date,
      sign_on_date: c.sign_on_date,
      readiness: c.readiness || c.status || '',
      status: c.status || c.readiness || '',
      crew_type: c.crew_type || c.vessel_type || '',
      vessel_type: c.vessel_type || c.crew_type || '',
      next_vessel: c.next_vessel,
      fleet: c.fleet,
      company: c.company,
      movement_type: c.movement_type,
      source_sheet: c.source_sheet,
      match_score: 100,
    })),
  };
}

// ========================
// API Functions
// ========================

export const crewApi = {
  // ========================
  // Health & Summary
  // ========================
  
  healthCheck: async () => {
    const response = await api.get('/api/health');
    return response.data;
  },

  getSummary: async (): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/api/summary');
    return response.data;
  },

  // ========================
  // Dashboard & Analytics
  // ========================
  
  getDashboardStats: async (): Promise<DashboardOverview> => {
    const response = await api.get<DashboardOverview>('/api/dashboard/stats');
    return response.data;
  },

  getAlerts: async (): Promise<Alert[]> => {
    const response = await api.get<Alert[]>('/api/dashboard/alerts');
    return response.data;
  },

  getAnalyticsOverview: async (): Promise<AnalyticsOverview> => {
    const response = await api.get<AnalyticsOverview>('/api/analytics/overview');
    return response.data;
  },

  getCrewByRank: async (): Promise<RankDistribution[]> => {
    const response = await api.get<RankDistribution[]>('/api/analytics/crew-by-rank');
    return response.data;
  },

  getCrewByNationality: async (): Promise<NationalityDistribution[]> => {
    const response = await api.get<NationalityDistribution[]>('/api/analytics/crew-by-nationality');
    return response.data;
  },

  getVesselCoverage: async (): Promise<VesselCoverage> => {
    const response = await api.get<VesselCoverage>('/api/analytics/vessel-coverage');
    return response.data;
  },

  // ========================
  // Crew Management
  // ========================
  
  getActiveCrew: async (params?: {
    rank?: string;
    nationality?: string;
    vessel?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; crew: CrewMember[] }> => {
    const response = await api.get<BackendCrewResponse>('/api/crew/active', {
      params: { limit: 100, ...params },
    });
    return transformCrewResponse(response.data);
  },

  getAvailableCrew: async (params?: {
    rank?: string;
    vessel_category?: string;
    availability_days?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; crew: CrewMember[] }> => {
    const response = await api.get<BackendCrewResponse>('/api/crew/available', {
      params: { limit: 100, ...params },
    });
    return transformCrewResponse(response.data);
  },

  getCrewById: async (cid: string): Promise<CrewDetailResponse> => {
    const response = await api.get<CrewDetailResponse>(`/api/crew/${cid}`);
    return response.data;
  },

  createCrew: async (data: CrewFormData): Promise<{ success: boolean; message: string; crew: CrewMember }> => {
    const response = await api.post('/api/crew', data);
    return response.data;
  },

  updateCrew: async (cid: string, data: CrewFormData): Promise<{ success: boolean; crew: CrewMember }> => {
    const response = await api.put(`/api/crew/${cid}`, data);
    return response.data;
  },

  deleteCrew: async (cid: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/crew/${cid}`);
    return response.data;
  },

  transferCrewStatus: async (
    cid: string, 
    newStatus: 'active' | 'available',
    data?: {
      vessel?: string;
      sign_on_date?: string;
      due_date?: string;
      sign_off_date?: string;
      available_from?: string;
    }
  ): Promise<{ success: boolean; message: string; crew: CrewMember }> => {
    const response = await api.post(`/api/crew/${cid}/transfer`, {
      new_status: newStatus,
      ...data
    });
    return response.data;
  },

  getCrewExperience: async (cid: string): Promise<{
    cid: string;
    experience: CrewExperience[];
    total_months: number;
    total_contracts: number;
    vessel_types_served: string[];
  }> => {
    const response = await api.get(`/api/crew/${cid}/experience`);
    return response.data;
  },

  getCrewAssignments: async (cid: string): Promise<Assignment[]> => {
    const response = await api.get(`/api/crew/${cid}/assignments`);
    return response.data;
  },

  // ========================
  // Vessel Management
  // ========================
  
  getVessels: async (params?: {
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; vessels: Vessel[] }> => {
    const response = await api.get('/api/vessels', {
      params: { limit: 200, ...params },
    });
    return response.data;
  },

  getVesselById: async (vesselId: number): Promise<VesselDetailResponse> => {
    const response = await api.get<VesselDetailResponse>(`/api/vessels/${vesselId}`);
    return response.data;
  },

  createVessel: async (data: VesselFormData): Promise<{ success: boolean; vessel: Vessel }> => {
    const response = await api.post('/api/vessels', data);
    return response.data;
  },

  updateVessel: async (vesselId: number, data: Partial<VesselFormData>): Promise<{ success: boolean; vessel: Vessel }> => {
    const response = await api.put(`/api/vessels/${vesselId}`, data);
    return response.data;
  },

  deleteVessel: async (vesselId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/vessels/${vesselId}`);
    return response.data;
  },

  getVesselCategories: async (): Promise<VesselCategory[]> => {
    const response = await api.get<VesselCategory[]>('/api/vessel-categories');
    return response.data;
  },

  // ========================
  // AI Vessel Position Suggestions
  // ========================

  suggestVesselPositions: async (params: {
    vessel_category: string;
    teu_capacity?: number;
    vessel_type?: string;
  }): Promise<VesselPositionSuggestionsResponse> => {
    const response = await api.post<VesselPositionSuggestionsResponse>('/api/vessels/suggest-positions', params);
    return response.data;
  },

  createVesselWithPositions: async (data: VesselCreateWithPositions): Promise<VesselCreateWithPositionsResponse> => {
    const response = await api.post<VesselCreateWithPositionsResponse>('/api/vessels/create-with-positions', data);
    return response.data;
  },

  // ========================
  // Relief Plans
  // ========================
  
  getReliefPlans: async (params?: {
    rank?: string;
    status?: string;
    vessel?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; plans: ReliefPlan[] }> => {
    const response = await api.get<{ total: number; plans: ReliefPlan[] }>('/api/relief-plans', {
      params: { limit: 100, ...params },
    });
    return response.data;
  },

  getReliefPlanById: async (planId: number): Promise<ReliefPlan> => {
    const response = await api.get<ReliefPlan>(`/api/relief-plans/${planId}`);
    return response.data;
  },

  createReliefPlan: async (data: ReliefPlanFormData): Promise<{ success: boolean; plan: ReliefPlan }> => {
    const response = await api.post('/api/relief-plans', data);
    return response.data;
  },

  updateReliefPlan: async (planId: number, data: Partial<ReliefPlanFormData>): Promise<{ success: boolean; plan: ReliefPlan }> => {
    const response = await api.put(`/api/relief-plans/${planId}`, data);
    return response.data;
  },

  deleteReliefPlan: async (planId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/relief-plans/${planId}`);
    return response.data;
  },

  assignCrewToRelief: async (planId: number, crewCid: string): Promise<{ success: boolean; plan: ReliefPlan }> => {
    const response = await api.post(`/api/relief-plans/${planId}/assign`, { crew_cid: crewCid });
    return response.data;
  },

  confirmReliefPlan: async (planId: number): Promise<{ success: boolean; plan: ReliefPlan }> => {
    const response = await api.post(`/api/relief-plans/${planId}/confirm`);
    return response.data;
  },

  // AI-Powered Relief Plan Suggestions
  suggestReliefCrew: async (planId: number, limit: number = 10): Promise<{
    plan_id: number;
    rank: string;
    vessel_name: string;
    due_date?: string;
    suggestions: Array<{
      cid: string;
      name: string;
      surname?: string;
      rank: string;
      nationality?: string;
      experience_months?: number;
      contracts?: number;
      crew_type?: string;
      readiness?: string;
      match_score: number;
      match_reasons: string[];
    }>;
    ai_reasoning: string;
  }> => {
    const response = await api.get(`/api/relief-plans/${planId}/suggest-crew`, {
      params: { limit }
    });
    return response.data;
  },

  bulkSuggestReliefCrew: async (planIds: number[], limitPerPlan: number = 5): Promise<{
    total_plans: number;
    results: Array<{
      plan_id: number;
      rank: string;
      vessel_name: string;
      due_date?: string;
      top_suggestion?: {
        cid: string;
        name: string;
        match_score: number;
      };
      total_candidates: number;
    }>;
  }> => {
    const response = await api.post('/api/relief-plans/bulk-suggest', {
      plan_ids: planIds,
      limit_per_plan: limitPerPlan
    });
    return response.data;
  },

  autoAssignReliefCrew: async (planId: number): Promise<{
    success: boolean;
    plan: ReliefPlan;
    assigned_crew: {
      cid: string;
      name: string;
      match_score: number;
    };
    ai_reasoning: string;
  }> => {
    const response = await api.post('/api/relief-plans/auto-assign', { plan_id: planId });
    return response.data;
  },

  getReliefStatistics: async (): Promise<{
    summary: {
      total: number;
      pending: number;
      confirmed: number;
      overdue: number;
      unassigned: number;
      completion_rate: number;
    };
    upcoming: {
      due_this_week: number;
      due_this_month: number;
    };
    by_rank: Array<{ rank: string; count: number }>;
    by_vessel: Array<{ vessel: string; count: number }>;
    alerts: {
      critical: number;
      warning: number;
      info: number;
    };
  }> => {
    const response = await api.get('/api/relief-plans/statistics');
    return response.data;
  },

  // ========================
  // Enhanced AI Relief Planning
  // ========================

  aiBulkSuggestReliefPlans: async (params?: {
    days_ahead?: number;
    auto_assign_threshold?: number;
  }): Promise<AIBulkSuggestResponse> => {
    const response = await api.post<AIBulkSuggestResponse>('/api/relief-plans/ai/bulk-suggest', {
      days_ahead: params?.days_ahead || 30,
      auto_assign_threshold: params?.auto_assign_threshold || 85,
    });
    return response.data;
  },

  aiExecuteBulkAssignments: async (params: {
    plan_ids: number[];
    auto_only?: boolean;
    threshold?: number;
  }): Promise<AIBulkExecuteResponse> => {
    const response = await api.post<AIBulkExecuteResponse>('/api/relief-plans/ai/execute-bulk', {
      plan_ids: params.plan_ids,
      auto_only: params.auto_only || false,
      threshold: params.threshold || 85,
    });
    return response.data;
  },

  aiGetReliefSchedule: async (params?: {
    vessel_id?: number;
    days_ahead?: number;
  }): Promise<AIReliefScheduleResponse> => {
    const response = await api.get<AIReliefScheduleResponse>('/api/relief-plans/ai/schedule', {
      params: {
        vessel_id: params?.vessel_id,
        days_ahead: params?.days_ahead || 90,
      },
    });
    return response.data;
  },

  aiCreateReliefFromSchedule: async (params: {
    crew_cid: string;
    auto_suggest_replacement?: boolean;
  }): Promise<AICreateFromScheduleResponse> => {
    const response = await api.post<AICreateFromScheduleResponse>('/api/relief-plans/ai/create-from-schedule', {
      crew_cid: params.crew_cid,
      auto_suggest_replacement: params.auto_suggest_replacement ?? true,
    });
    return response.data;
  },

  aiOptimizeReliefTiming: async (planId: number): Promise<AIOptimizeTimingResponse> => {
    const response = await api.get<AIOptimizeTimingResponse>(`/api/relief-plans/${planId}/ai/optimize-timing`);
    return response.data;
  },

  // AI Auto-Generate Relief Plans
  aiAutoGenerateReliefPlans: async (params: {
    days_ahead: number;
    include_existing?: boolean;
  }): Promise<{
    success: boolean;
    created_count: number;
    existing_count: number;
    skipped_count: number;
    created_plans: Array<{
      plan_id: number;
      vessel: string;
      rank: string;
      outgoing_crew: string;
      due_date?: string;
      suggested_replacement?: {
        cid: string;
        name: string;
        nationality?: string;
        match_score: number;
      };
    }>;
    skipped_plans: Array<{
      cid: string;
      name: string;
      vessel: string;
      rank: string;
      reason: string;
    }>;
    ai_summary: string;
  }> => {
    const response = await api.post('/api/relief-plans/ai/auto-generate', params);
    return response.data;
  },

  // AI Conflict Detection
  aiDetectConflicts: async (daysAhead: number = 90): Promise<{
    total_plans_checked: number;
    conflicts: Array<{
      type: string;
      severity: string;
      message: string;
      plan_id?: number;
      vessel?: string;
      rank?: string;
      due_date?: string;
      days_until_due?: number;
      crew_cid?: string;
      crew_name?: string;
      count?: number;
      ranks?: string[];
      plan_ids?: number[];
      assignments?: Array<{
        plan_id: number;
        vessel: string;
        rank: string;
        due_date?: string;
      }>;
    }>;
    warnings: Array<{
      type: string;
      severity: string;
      message: string;
      plan_id?: number;
      vessel?: string;
      rank?: string;
      due_date?: string;
      days_until_due?: number;
    }>;
    conflict_count: number;
    warning_count: number;
    ai_summary: string;
    is_healthy: boolean;
  }> => {
    const response = await api.get('/api/relief-plans/ai/conflicts', {
      params: { days_ahead: daysAhead }
    });
    return response.data;
  },

  // Relief Plan Analytics
  getReliefPlanAnalytics: async (daysRange: number = 90): Promise<{
    period: { start: string; end: string; days: number };
    overview: {
      total_plans: number;
      confirmed_plans: number;
      completion_rate: number;
      avg_days_to_assignment: number;
    };
    monthly_trend: Array<{
      month?: string;
      total: number;
      confirmed: number;
    }>;
    by_rank: Array<{
      rank: string;
      total: number;
      assignment_rate: number;
    }>;
    top_vessels: Array<{
      vessel: string;
      count: number;
    }>;
    ai_insights: string[];
  }> => {
    const response = await api.get('/api/relief-plans/ai/analytics', {
      params: { days_range: daysRange }
    });
    return response.data;
  },

  // Search Available Crew for Relief
  searchAvailableCrewForRelief: async (params: {
    rank: string;
    query?: string;
    exclude_cids?: string[];
    limit?: number;
  }): Promise<{
    total: number;
    crew: Array<{
      cid: string;
      name: string;
      surname?: string;
      rank: string;
      nationality?: string;
      due_date?: string;
      readiness?: string;
      crew_type?: string;
      match_score: number;
      match_reasons: string[];
    }>;
  }> => {
    const response = await api.get('/api/relief-plans/search-crew', {
      params: {
        rank: params.rank,
        query: params.query,
        exclude_cids: params.exclude_cids?.join(','),
        limit: params.limit || 20
      }
    });
    return response.data;
  },

  // AI Bulk Create Relief Plans
  aiBulkCreateReliefPlans: async (plans: Array<{
    vessel_name: string;
    rank: string;
    outgoing_cid?: string;
    outgoing_name?: string;
    incoming_cid?: string;
    incoming_name?: string;
    due_date: string;
    status?: string;
    notes?: string;
  }>): Promise<{
    success: boolean;
    created_count: number;
    error_count: number;
    created_plans: Array<{
      plan_id: number;
      vessel: string;
      rank: string;
      suggested_replacement?: {
        cid: string;
        name: string;
        match_score: number;
      };
    }>;
    errors: Array<{
      data: unknown;
      error: string;
    }>;
    ai_summary: string;
  }> => {
    const response = await api.post('/api/relief-plans/ai/bulk-create', plans);
    return response.data;
  },

  // ========================
  // Assignment Workflow
  // ========================
  
  createAssignment: async (data: {
    crew_cid: string;
    vessel_id: number;
    vessel_name: string;
    rank: string;
    sign_on_date?: string;
    expected_duration_months?: number;
    notes?: string;
  }): Promise<{ success: boolean; assignment: Assignment }> => {
    const response = await api.post('/api/assignments', data);
    return response.data;
  },

  getPendingAssignments: async (): Promise<Assignment[]> => {
    const response = await api.get('/api/assignments/pending');
    return response.data;
  },

  getWorkflowStats: async (): Promise<WorkflowStats> => {
    const response = await api.get<WorkflowStats>('/api/assignments/workflow-stats');
    return response.data;
  },

  matchCrewToVessel: async (vesselId: number, rank: string, vesselCategory?: string): Promise<{
    total: number;
    matches: CrewMember[];
  }> => {
    const response = await api.post('/api/assignments/match', {
      vessel_id: vesselId,
      rank,
      vessel_category: vesselCategory,
    });
    return response.data;
  },

  // ========================
  // Vessel Crew Planning (AI Suggestions)
  // ========================
  
  suggestCrewPlan: async (request: {
    vessel_id: number;
    vessel_name: string;
    vessel_category: string;
    required_ranks: string[];
    due_date?: string;
  }): Promise<VesselCrewPlan> => {
    const response = await api.post<VesselCrewPlan>('/api/vessel-crew-plan/suggest', {
      vessel_id: request.vessel_id,
      vessel_name: request.vessel_name,
      vessel_category: request.vessel_category,
      positions: request.required_ranks,
    });
    return response.data;
  },

  getCandidatesForRank: async (
    vesselId: number,
    rank: string,
    vesselCategory: string
  ): Promise<CrewMember[]> => {
    const response = await api.post<{ position: string; alternatives: Array<{
      cid: string;
      name: string;
      surname?: string;
      nationality?: string;
      vessel_type?: string;
      match_score?: number;
    }> }>('/api/vessel-crew-plan/alternatives', {
      position: rank,
      vessel_category: vesselCategory,
      exclude_cids: []
    });
    // Transform alternatives to CrewMember format
    return response.data.alternatives.map(alt => ({
      cid: alt.cid,
      rank: rank,
      name: alt.name,
      surname: alt.surname || '',
      nationality: alt.nationality || 'N/A',
      vessel_type: alt.vessel_type || '',
      crew_type: alt.vessel_type || '',
      match_score: alt.match_score || 80,
    }));
  },

  confirmCrewPlan: async (
    vesselId: number,
    vesselName: string,
    assignments: Array<{ position: string; crew_cid: string; crew_name: string; }>
  ): Promise<{ success: boolean; assignments_created: number }> => {
    const response = await api.post('/api/vessel-crew-plan/confirm', {
      vessel_id: vesselId,
      vessel_name: vesselName,
      assignments: assignments,
    });
    return response.data;
  },

  getVesselCrewStatus: async (vesselId: number): Promise<VesselCrewStatus> => {
    const response = await api.get<VesselCrewStatus>(`/api/vessel-crew-plan/${vesselId}`);
    return response.data;
  },

  // ========================
  // Experience Matrix
  // ========================
  
  getExperienceMatrix: async (params?: {
    rank?: string;
    min_months?: number;
  }): Promise<ExperienceMatrixEntry[]> => {
    const response = await api.get<ExperienceMatrixEntry[]>('/api/experience-matrix', { params });
    return response.data;
  },

  getExperienceByCategory: async (): Promise<ExperienceByCategory[]> => {
    const response = await api.get<ExperienceByCategory[]>('/api/experience-matrix/by-vessel-category');
    return response.data;
  },

  // ========================
  // Reference Data
  // ========================
  
  getRanks: async (): Promise<Rank[]> => {
    const response = await api.get<Rank[]>('/api/ranks');
    return response.data;
  },

  getNationalities: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/api/nationalities');
    return response.data;
  },

  getStatusOptions: async (): Promise<StatusOption[]> => {
    const response = await api.get<StatusOption[]>('/api/status-options');
    return response.data;
  },

  // ========================
  // Search
  // ========================
  
  search: async (query: string): Promise<SearchResult> => {
    const response = await api.get<BackendSearchResponse>('/api/search/ai', {
      params: { q: query },
    });
    return transformSearchResponse(response.data);
  },

  searchWithFilters: async (filters: {
    rank?: string;
    vessel_category?: string;
    nationality?: string;
    min_experience_months?: number;
    availability_days?: number;
  }): Promise<SearchResult> => {
    const response = await api.post('/api/search', filters);
    return response.data;
  },

  // ========================
  // Activity Log
  // ========================
  
  getActivityLog: async (params?: {
    entity_type?: string;
    limit?: number;
  }): Promise<ActivityLogEntry[]> => {
    const response = await api.get<ActivityLogEntry[]>('/api/activity-log', { params });
    return response.data;
  },
};

// ========================
// Legacy Helper Functions
// ========================

export const getCrewSummary = async () => {
  const summary = await crewApi.getSummary();
  return {
    active_masters: summary.by_rank?.MSTR || 0,
    chief_officers: summary.by_rank?.COFF || 0,
    chief_engineers: summary.by_rank?.CENG || 0,
    total_vessels: summary.total_vessels || 0,
    total_active: summary.total_active || 0,
    total_available: summary.total_available || 0,
    third_engineers: summary.by_rank?.['3ENG'] || 0,
    fourth_engineers: summary.by_rank?.['4ENG'] || 0,
  };
};

export const getVesselCategories = async (): Promise<VesselCategory[]> => {
  return crewApi.getVesselCategories();
};

export const getActiveCrew = async (rank?: string): Promise<CrewMember[]> => {
  const result = await crewApi.getActiveCrew({ rank });
  return result.crew;
};

export const getAvailableCrew = async (rank?: string): Promise<CrewMember[]> => {
  const result = await crewApi.getAvailableCrew({ rank });
  return result.crew;
};

export const getReliefPlans = async (rank?: string): Promise<ReliefPlan[]> => {
  const result = await crewApi.getReliefPlans({ rank });
  return result.plans;
};

export default api;
